import { useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { Check, Loader2, UserRound, X } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/shadcn/avatar';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useAbilities } from '@/hooks/use-abilities';
import { useDebounce } from '@/hooks/use-debounce';
import { cn } from '@/lib/utils';

export interface UserSelectUser {
    id: string;
    first_name: string;
    last_name: string;
    role?: string;
    position?: string;
    avatar?: string;
    email?: string;
}

type SeedUserInput = Partial<UserSelectUser> & { uuid?: string };

type ApiUserRaw = {
    uuid?: string;
    id?: string;
    first_name?: string;
    last_name?: string;
    role?: string;
    position?: string;
    avatar?: string;
    email?: string;
};

function normalizeUser(raw: ApiUserRaw | SeedUserInput): UserSelectUser | null {
    const id = String((raw as ApiUserRaw).uuid ?? raw.id ?? '');
    if (!id) return null;
    return {
        id,
        first_name: raw.first_name ?? '',
        last_name: raw.last_name ?? '',
        role: raw.role ?? undefined,
        position: raw.position ?? undefined,
        avatar: raw.avatar ?? undefined,
        email: raw.email ?? undefined,
    };
}

function getInitials(user: UserSelectUser): string {
    const f = user.first_name?.[0] ?? '';
    const l = user.last_name?.[0] ?? '';
    return (f + l).toUpperCase() || '?';
}

// ─── Shared user row ──────────────────────────────────────────────────────────

interface UserRowProps {
    user: UserSelectUser;
    className?: string;
}

export function UserRow({ user, className }: UserRowProps) {
    const fullName = `${user.first_name} ${user.last_name}`.trim() || '—';
    return (
        <span className={cn('flex min-w-0 flex-1 items-center gap-2', className)}>
            <Avatar className="h-6 w-6 shrink-0">
                <AvatarImage src={user.avatar} alt={fullName} />
                <AvatarFallback className="text-[10px]">{getInitials(user)}</AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 truncate text-sm">{fullName}</span>
            {user.role && (
                <Badge variant="secondary" className="shrink-0 text-xs">
                    {user.role}
                </Badge>
            )}
        </span>
    );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface UserSelectBase {
    seedUsers?: SeedUserInput[];
    /** Filter the list to only users whose role matches one of these strings (case-insensitive). */
    roles?: string[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
}

export interface UserSelectSingleProps extends UserSelectBase {
    multiple?: false;
    value?: string | null;
    onValueChange: (value: string | null) => void;
    allowClear?: boolean;
}

export interface UserSelectMultipleProps extends UserSelectBase {
    multiple: true;
    values?: string[];
    onValuesChange: (values: string[]) => void;
}

export type UserSelectProps = UserSelectSingleProps | UserSelectMultipleProps;

// ─── Component ────────────────────────────────────────────────────────────────

export function UserSelect(props: UserSelectProps) {
    const { seedUsers = [], roles, placeholder = 'Select user…', disabled = false, className } = props;
    const isMultiple = props.multiple === true;

    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [allUsers, setAllUsers] = useState<UserSelectUser[]>(() => seedUsers.map(normalizeUser).filter((u): u is UserSelectUser => u !== null));

    const debouncedSearch = useDebounce(searchTerm, 250);
    const { can } = useAbilities();

    // Fetch full user list on first open; seeds may be a partial list.
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            return;
        }
        if (hasFetched) return;
        // Skip the request for users who cannot assign — it would only 403.
        if (!can('assign-employees')) return;

        let cancelled = false;
        setLoading(true);

        void (async () => {
            try {
                const res = await axios.get<{ users: ApiUserRaw[] }>('/api/users/assignable');
                if (cancelled) return;
                const fetched = (res.data?.users ?? []).map(normalizeUser).filter((u): u is UserSelectUser => u !== null);
                setAllUsers((prev) => {
                    const map = new Map(prev.map((u) => [u.id, u]));
                    fetched.forEach((u) => map.set(u.id, u));
                    return Array.from(map.values());
                });
                setHasFetched(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, hasFetched, can]);

    const filteredUsers = useMemo(() => {
        let list = allUsers;
        if (roles && roles.length > 0) {
            const normalized = roles.map((r) => r.toLowerCase());
            list = list.filter((u) => u.role && normalized.includes(u.role.toLowerCase()));
        }
        const term = debouncedSearch.trim().toLowerCase();
        if (!term) return list;
        return list.filter((u) => {
            const name = `${u.first_name} ${u.last_name}`.toLowerCase();
            return name.includes(term) || (u.email ?? '').toLowerCase().includes(term);
        });
    }, [allUsers, roles, debouncedSearch]);

    // Derived selected state — computed unconditionally
    const singleValue = !isMultiple ? ((props as UserSelectSingleProps).value ?? null) : null;
    const multiValuesRaw = isMultiple ? (props as UserSelectMultipleProps).values : undefined;
    const multiValues = useMemo(() => multiValuesRaw ?? [], [multiValuesRaw]);

    const selectedUser = useMemo(() => (singleValue ? (allUsers.find((u) => u.id === singleValue) ?? null) : null), [allUsers, singleValue]);

    const selectedUsers = useMemo(() => allUsers.filter((u) => multiValues.includes(u.id)), [allUsers, multiValues]);

    const selectedIds = isMultiple ? multiValues : singleValue ? [singleValue] : [];

    const handleSelect = (userId: string) => {
        if (isMultiple) {
            const { onValuesChange } = props as UserSelectMultipleProps;
            const next = multiValues.includes(userId) ? multiValues.filter((id) => id !== userId) : [...multiValues, userId];
            onValuesChange(next);
        } else {
            const { onValueChange } = props as UserSelectSingleProps;
            onValueChange(userId === singleValue ? null : userId);
            setOpen(false);
        }
    };

    const handleClearSingle = () => {
        (props as UserSelectSingleProps).onValueChange(null);
    };

    const allowClear = !isMultiple ? ((props as UserSelectSingleProps).allowClear ?? true) : false;

    return (
        <Popover modal open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={disabled}
                    className={cn(
                        'w-full justify-between font-normal',
                        !isMultiple && !selectedUser && 'text-muted-foreground',
                        isMultiple && selectedUsers.length === 0 && 'text-muted-foreground',
                        className,
                    )}
                >
                    {/* Single mode trigger */}
                    {!isMultiple && (
                        <>
                            {selectedUser ? (
                                <UserRow user={selectedUser} />
                            ) : (
                                <span className="flex items-center gap-2">
                                    <UserRound className="h-4 w-4 shrink-0 opacity-50" />
                                    {placeholder}
                                </span>
                            )}
                            {selectedUser && allowClear && !disabled ? (
                                <span
                                    role="button"
                                    tabIndex={0}
                                    aria-label="Clear selection"
                                    className="text-muted-foreground hover:text-foreground ml-auto shrink-0 rounded p-0.5"
                                    onPointerDown={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleClearSingle();
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleClearSingle();
                                        }
                                    }}
                                >
                                    <X className="h-3.5 w-3.5" />
                                </span>
                            ) : null}
                        </>
                    )}

                    {/* Multiple mode trigger */}
                    {isMultiple && (
                        <>
                            {selectedUsers.length === 0 ? (
                                <span className="flex items-center gap-2">
                                    <UserRound className="h-4 w-4 shrink-0 opacity-50" />
                                    {placeholder}
                                </span>
                            ) : selectedUsers.length === 1 ? (
                                <UserRow user={selectedUsers[0]} />
                            ) : (
                                <span className="flex min-w-0 items-center gap-2">
                                    <div className="flex shrink-0 -space-x-1.5">
                                        {selectedUsers.slice(0, 3).map((u) => (
                                            <Avatar key={u.id} className="ring-background h-6 w-6 ring-2">
                                                <AvatarImage src={u.avatar} />
                                                <AvatarFallback className="text-[10px]">{getInitials(u)}</AvatarFallback>
                                            </Avatar>
                                        ))}
                                    </div>
                                    <span className="text-foreground truncate text-sm">{selectedUsers.length} users selected</span>
                                </span>
                            )}
                        </>
                    )}
                </Button>
            </PopoverTrigger>

            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                <Command shouldFilter={false}>
                    <CommandInput placeholder="Search…" value={searchTerm} onValueChange={setSearchTerm} />
                    <CommandList className="max-h-64">
                        {loading ? (
                            <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Loading…
                            </div>
                        ) : (
                            <>
                                <CommandEmpty>No users found.</CommandEmpty>
                                <CommandGroup>
                                    {filteredUsers.map((user) => {
                                        const selected = selectedIds.includes(user.id);
                                        return (
                                            <CommandItem
                                                key={user.id}
                                                value={user.id}
                                                onSelect={() => handleSelect(user.id)}
                                                className="cursor-pointer"
                                            >
                                                {isMultiple ? (
                                                    <span
                                                        className={cn(
                                                            'mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                                                            selected
                                                                ? 'border-primary bg-primary text-primary-foreground'
                                                                : 'border-muted-foreground/40',
                                                        )}
                                                    >
                                                        {selected && <Check className="h-3 w-3" />}
                                                    </span>
                                                ) : (
                                                    <Check className={cn('mr-2 h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
                                                )}
                                                <UserRow user={user} />
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            </>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
